/**
 * Shared pull-to-refresh helpers for mobile browsers.
 * The app shell locks body scroll and uses inner scroll containers,
 * which blocks native browser pull-to-refresh.
 */

import { Capacitor } from '@capacitor/core';

export const PULL_THRESHOLD_PX = 72;
export const SCROLL_TOP_TOLERANCE = 1;
export const MAX_HORIZONTAL_DRIFT = 40;

const PRIMARY_SCROLL_MIN_HEIGHT_RATIO = 0.5;

const KNOWN_PRIMARY_SELECTOR_LIST = [
  'main.content-safe-scroll',
  '[data-auth-scroll-container="true"]',
  '[data-scroll-container]',
] as const;

const IGNORE_SELECTORS = [
  '[data-ptr-ignore]',
  '[data-sheet-scroll-container]',
  '[role="dialog"]',
].join(', ');

function matchesKnownPrimaryScrollRoot(el: HTMLElement): boolean {
  if (el.hasAttribute('data-ptr-root')) return true;
  return KNOWN_PRIMARY_SELECTOR_LIST.some((selector) => el.matches(selector));
}

export function isMobileBrowserPullToRefreshEnabled(): boolean {
  if (typeof document === 'undefined') return false;
  if (Capacitor.isNativePlatform()) return false;
  return document.documentElement.classList.contains('is-real-mobile');
}

function isIgnoredTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest(IGNORE_SELECTORS));
}

function getScrollTop(el: HTMLElement): number {
  if (el === document.documentElement || el === document.body) {
    return window.scrollY || document.documentElement.scrollTop || 0;
  }
  return el.scrollTop;
}

function isScrollableY(el: HTMLElement): boolean {
  const { overflowY } = getComputedStyle(el);
  if (overflowY !== 'auto' && overflowY !== 'scroll') return false;
  return el.scrollHeight > el.clientHeight + 1;
}

function isPrimaryScrollContainer(el: HTMLElement): boolean {
  if (!isScrollableY(el)) return false;
  const minHeight = window.innerHeight * PRIMARY_SCROLL_MIN_HEIGHT_RATIO;
  return el.clientHeight >= minHeight;
}

function getDocumentScrollRoot(): HTMLElement | null {
  const scrollingElement = document.scrollingElement;
  if (!(scrollingElement instanceof HTMLElement)) return null;

  const bodyOverflow = document.body.style.overflowY;
  const canDocumentScroll =
    bodyOverflow === 'auto' ||
    bodyOverflow === 'scroll' ||
    scrollingElement.scrollHeight > window.innerHeight + 1;

  return canDocumentScroll ? scrollingElement : null;
}

export function findPullToRefreshRootFromTarget(
  target: EventTarget | null,
): HTMLElement | null {
  if (isIgnoredTarget(target)) return null;

  let node = target instanceof Element ? target : null;

  while (node && node !== document.documentElement) {
    if (matchesKnownPrimaryScrollRoot(node)) {
      return node;
    }
    if (isPrimaryScrollContainer(node)) {
      return node;
    }
    node = node.parentElement;
  }

  return getDocumentScrollRoot();
}

export function isScrollRootAtTop(el: HTMLElement): boolean {
  return getScrollTop(el) <= SCROLL_TOP_TOLERANCE;
}
